/**
 * Parsing for the /api/billing/estimate-billing response.
 *
 * The billing manager kept the route but repointed it at estimateBillingV2, so
 * the URL is unchanged and the body is not: it is now an object carrying a
 * `resellers` array, where it used to be a one-element array of flat
 * estimate_maa / estimate_mau / estimate_bandwidth blocks.
 *
 * Both the Billing page and the dashboard tile read the same body, so the
 * parsing lives here rather than twice inside JSX.
 */

// Pricing types that make each stat card meaningful. A contract only earns a
// card when one of its pricing rows actually prices that metric.
const MAA_PRICING_TYPES = ['MAA_MAA_PAID'];
const MAU_PRICING_TYPES = ['MAU_MAU_PAID', 'MAU_MAU_TRIAL', 'MAU_BANDWIDTH_FREEMIUM'];
const BANDWIDTH_PRICING_TYPES = ['BANDWIDTH_FREEMIUM', 'MAU_BANDWIDTH_ALLTYPES', 'MAU_BANDWIDTH_TRIAL'];

const num = (value, fallback = 0) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const decimals = { minimumFractionDigits: 2, maximumFractionDigits: 2 };

/**
 * Format a gigabyte figure. v2 reports bandwidth in GB — v1 reported bytes, so
 * anything still passing these values through formatBytes reads ~1000x low.
 */
export const formatGb = (gb) => {
  const value = num(gb);
  if (value >= 1024) return `${(value / 1024).toLocaleString(undefined, decimals)} TB`;
  if (value >= 1) return `${value.toLocaleString(undefined, decimals)} GB`;
  if (value >= 1 / 1024) return `${(value * 1024).toLocaleString(undefined, decimals)} MB`;
  if (value > 0) return `${(value * 1024 * 1024).toLocaleString(undefined, decimals)} KB`;
  return '0.00 GB';
};

export const formatUsd = (amount) => `$${num(amount).toLocaleString(undefined, decimals)}`;

export const formatCount = (count) => num(count).toLocaleString();

/**
 * One priced line, shaped for the breakdown. The projected and the actual item
 * lists carry the same fields, so both go through here.
 */
const normalizeItem = (item, index) => ({
  key: `${item.type}-${item.pricing_id ?? 'none'}-${index}`,
  // Index-free, so the same line can be found in the projected and the actual
  // list even when a slab change drops a row from one of them.
  matchKey: `${item.type}-${item.pricing_id ?? 'none'}`,
  type: item.type,
  description: item.description ?? item.type,
  count: num(item.count),
  bandwidthGb: num(item.bandwidth_gb),
  rate: num(item.rate),
  amount: num(item.amount),
  // Present only once the minimum commitment has zeroed the usage lines.
  usageAmount: item.usage_amount === undefined ? null : num(item.usage_amount)
});

/**
 * Flatten the v2 body into what the UI renders.
 *
 * @param {object} body response.data.body
 * @returns {object|null} null when the response carries no reseller
 */
export const normalizeEstimate = (body) => {

  const reseller = body?.resellers?.[0];
  if (!reseller) {
    return null;
  }

  // A closed window has projection_factor 1, which makes the actual and the
  // estimate fields identical — so this one flag covers both cases.
  const isComplete = body.is_window_complete === true;

  const usage = (isComplete ? reseller.usage : reseller.estimate_usage) ?? {};
  const rawItems = (isComplete ? reseller.invoice_items : reseller.estimate_invoice_items) ?? [];
  const pricingTypes = (reseller.contract?.pricing ?? []).map((row) => row.type);

  // The bandwidth breakdown only exists on `usage` — `estimate_usage.bandwidth_gb`
  // is a bare number. The projected totals ride along inside the same block.
  const bandwidth = reseller.usage?.bandwidth_gb ?? {};
  // Actuals to date, kept alongside the projection so the UI can show both. Once
  // the window closes the two blocks carry the same numbers.
  const actualUsage = reseller.usage ?? {};

  return {
    error: reseller.error ?? null,
    hasContract: reseller.has_contract === true,
    hasUsageData: reseller.has_usage_data === true,

    billingMonth: body.billing_month ?? '',
    fromDate: body.from_date ?? null,
    toDate: body.to_date ?? null,
    isComplete,
    projectionFactor: num(body.projection_factor, 1),
    windowDays: num(body.window_days),
    elapsedDays: num(body.elapsed_days),

    invoiceAmount: num(isComplete ? reseller.final_amount : reseller.estimate_final_amount),
    usageAmount: num(isComplete ? reseller.usage_amount : reseller.estimate_usage_amount),
    minimumCommitment: num(reseller.minimum_commitment),
    minimumCommitmentApplied:
      (isComplete ? reseller.minimum_commitment_applied : reseller.estimate_minimum_commitment_applied) === true,

    paidMau: num(usage.paid_mau),
    trialMau: num(usage.trial_mau),
    connectedMau: num(usage.connected_mau),
    // Omitted rather than zeroed when the contract does not price MAA, and
    // "not billed" is not the same statement as "no accounts".
    maa: usage.maa === undefined || usage.maa === null ? null : num(usage.maa),

    bandwidth: {
      total: num(isComplete ? bandwidth.total : bandwidth.estimate_total),
      free: num(isComplete ? bandwidth.free : bandwidth.estimate_free),
      // The split and the per-cap rows are actuals only — the response carries
      // no projected counterpart, so the UI labels them "to date".
      paid: num(bandwidth.paid),
      trial: num(bandwidth.trial),
      freemium: num(bandwidth.freemium),
      caps: (bandwidth.caps ?? []).map((cap) => ({
        capGb: num(cap.cap_gb),
        aboveCapGb: num(cap.above_cap_gb),
        aboveCapMau: num(cap.above_cap_mau),
        belowCapGb: num(cap.below_cap_gb),
        belowCapMau: num(cap.below_cap_mau)
      }))
    },

    // What actually happened so far this window. The fields above are projected
    // to month end while the window is open, so a card that shows a projected
    // headline next to an actual split needs both to label them apart.
    actual: {
      paidMau: num(actualUsage.paid_mau),
      trialMau: num(actualUsage.trial_mau),
      connectedMau: num(actualUsage.connected_mau),
      maa: actualUsage.maa === undefined || actualUsage.maa === null ? null : num(actualUsage.maa),
      bandwidthTotal: num(bandwidth.total),
      bandwidthFree: num(bandwidth.free),
      // What the window has earned so far, priced off the actual counts — the
      // projected slab can differ, so this is not the headline scaled down.
      invoiceAmount: num(reseller.final_amount),
      usageAmount: num(reseller.usage_amount),
      minimumCommitmentApplied: reseller.minimum_commitment_applied === true,
      items: (reseller.invoice_items ?? []).map(normalizeItem)
    },

    items: rawItems.map(normalizeItem),

    // A contract with no pricing rows is billed on default_price_usd × paid
    // users, which is a MAU line — so it still earns the MAU card.
    hasMaa: pricingTypes.some((type) => MAA_PRICING_TYPES.includes(type)),
    hasMau: pricingTypes.length === 0 || pricingTypes.some((type) => MAU_PRICING_TYPES.includes(type)),
    hasBandwidth: pricingTypes.some((type) => BANDWIDTH_PRICING_TYPES.includes(type)),

    dailyStats: (reseller.date_wise ?? [])
      .map((day) => ({
        date: day.date,
        paid_users: num(day.paid),
        trial_users: num(day.trial),
        bandwidth_gb: num(day.bandwidth_gb),
        paid_bandwidth_gb: num(day.paid_bandwidth_gb),
        trial_bandwidth_gb: num(day.trial_bandwidth_gb)
      }))
      .sort((a, b) => String(a.date).localeCompare(String(b.date)))
  };
};
